import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Loader2, FileText, Search, RefreshCw, Lock, Eye, EyeOff,
  Wrench, Headphones, Settings, ChevronDown, UserPlus,
} from 'lucide-react';

import { Button, Card } from '../ui-v2';
import { getPlantillasNotif } from '../intervenciones/api';
import EditorPlantilla from '../intervenciones/plantillas/EditorPlantilla';

/**
 * PlantillasNotificaciones — editor GLOBAL del catálogo de plantillas
 * (notificacion_plantillas), transversal a los módulos.
 *
 * Fase 23: movido desde intervenciones/ a configuracion/. Agrupa por `modulo`
 * (intervenciones/soporte/sistema) con selector opcional + búsqueda de texto.
 *
 * Ruta: /configuracion/plantillas-notificaciones
 * Permiso: intervenciones.campanas.manage (Super Administrador en el sidebar).
 */

/* `moduloDe` cae en 'sistema' cuando el modulo no esta en este mapa. Asi que anadir filas con un
 * `modulo` nuevo NO basta: sin su entrada aqui, aparecen bajo «Sistema» y nadie las encuentra donde
 * las busca. Es el paso que se olvida, porque el dato ya esta bien en la base. */
const SECCIONES = {
  intervenciones: { label: 'Intervenciones', icono: Wrench, color: 'text-blue-700' },
  soporte: { label: 'Soporte', icono: Headphones, color: 'text-purple-700' },
  incorporaciones: { label: 'Incorporaciones', icono: UserPlus, color: 'text-emerald-700' },
  sistema: { label: 'Sistema', icono: Settings, color: 'text-slate-700' },
};
const ORDEN_SECCIONES = ['intervenciones', 'soporte', 'incorporaciones', 'sistema'];

function moduloDe(p) {
  return SECCIONES[p.modulo] ? p.modulo : 'sistema';
}

export default function PlantillasNotificaciones() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seleccionada, setSeleccionada] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [seccion, setSeccion] = useState(''); // '' = todas

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getPlantillasNotif();
      setData(res.data || []);
      setSeleccionada((sel) => {
        if (sel) {
          const igual = (res.data || []).find((p) => p.id === sel.id);
          if (igual) return igual;
        }
        return (res.data || [])[0] || null;
      });
    } catch (e) {
      console.error('[PlantillasNotificaciones]', e);
      setError(e?.response?.data?.error || 'No se pudieron cargar las plantillas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Conteos por sección — sobre TODAS las plantillas (no cambian con el filtro).
  const conteos = useMemo(() => {
    /* Desde el mapa, no a mano: con las claves escritas una a una, un modulo nuevo daba
     * `undefined + 1 = NaN` y su seccion no se podia contar ni filtrar. */
    const c = Object.fromEntries(ORDEN_SECCIONES.map((m) => [m, 0]));
    for (const p of data) c[moduloDe(p)] += 1;
    return c;
  }, [data]);

  // Filtro combinado: sección (AND) + búsqueda de texto (AND).
  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return data
      .filter((p) => !seccion || moduloDe(p) === seccion)
      .filter((p) => !q
        || (p.tipo || '').toLowerCase().includes(q)
        || (p.descripcion || '').toLowerCase().includes(q));
  }, [data, seccion, busqueda]);

  // Agrupación por sección (orden fijo). Siempre con cabecera, aunque haya 1 grupo.
  const grupos = useMemo(() => {
    const map = new Map();
    for (const p of filtradas) {
      const m = moduloDe(p);
      if (!map.has(m)) map.set(m, []);
      map.get(m).push(p);
    }
    return ORDEN_SECCIONES
      .filter((m) => map.has(m))
      .map((m) => [m, map.get(m)]);
  }, [filtradas]);

  function onPlantillaActualizada(actualizada) {
    setData((prev) => prev.map((p) =>
      p.id === actualizada.id ? { ...p, ...actualizada } : p
    ));
    setSeleccionada((sel) => sel?.id === actualizada.id ? { ...sel, ...actualizada } : sel);
  }

  return (
    <div className="min-h-full">
      {/* MINI-HEADER STICKY */}
      <div className="sticky top-14 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-slate-400 shrink-0" />
            <h1 className="text-sm font-medium text-slate-900">Plantillas de notificación</h1>
            {data.length > 0 && (
              <span className="hidden md:inline text-xs text-slate-500">
                {data.length} {data.length === 1 ? 'plantilla' : 'plantillas'}
              </span>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={cargar} disabled={loading}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Actualizar
          </Button>
        </div>
      </div>

      <div className="px-6 py-6">
        {error && (
          <div className="mb-4 border border-red-200 bg-red-50 rounded-lg p-3 text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading && data.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
            Cargando plantillas…
          </div>
        ) : data.length === 0 ? (
          <Card className="p-10 text-center">
            <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm text-slate-600">No hay plantillas todavía.</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
            {/* LISTA */}
            <Card className="overflow-hidden p-0 lg:col-span-1">
              {/* Buscador + selector de sección */}
              <div className="px-3 py-2.5 border-b border-slate-100 space-y-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar tipo o descripción…"
                    className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-md"
                  />
                </div>
                <div className="relative">
                  <select
                    value={seccion}
                    onChange={(e) => setSeccion(e.target.value)}
                    className="w-full pl-3 pr-8 py-1.5 text-xs border border-slate-200 rounded-md appearance-none bg-white"
                  >
                    <option value="">Todas las secciones</option>
                    {/* SE GENERAN DEL MAPA, no a mano.
                        Estaban escritas una a una, asi que anadir un modulo eran DOS cambios y solo se
                        hizo uno: las tres plantillas de Incorporaciones se agrupaban bien y **no habia
                        ninguna opcion que las seleccionara** —24 en el titulo y 12+9 en el filtro—. Y al
                        dejar de caer en «sistema», tampoco aparecian ahi.
                        Generandolas, el mapa es la unica fuente y no se pueden desincronizar. Se ocultan
                        las vacias, que era lo que hacia la condicion de «sistema». */}
                    {ORDEN_SECCIONES.filter((m) => conteos[m] > 0).map((m) => (
                      <option key={m} value={m}>{SECCIONES[m].label} ({conteos[m]})</option>
                    ))}
                  </select>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              {filtradas.length === 0 ? (
                <p className="px-4 py-8 text-center text-xs text-slate-400">
                  Ninguna plantilla coincide con los filtros aplicados.
                </p>
              ) : (
                <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
                  {grupos.map(([modulo, plantillas]) => {
                    const meta = SECCIONES[modulo];
                    const Icono = meta.icono;
                    return (
                      <div key={modulo}>
                        <div className="px-4 py-1.5 bg-slate-50 border-y border-slate-100 flex items-center gap-1.5 sticky top-0">
                          <Icono className={`w-3.5 h-3.5 ${meta.color}`} />
                          <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                            {meta.label}
                          </span>
                          <span className="text-[11px] text-slate-400">({plantillas.length})</span>
                        </div>
                        <ul className="divide-y divide-slate-100">
                          {plantillas.map((p) => {
                            const activa = !!p.activa;
                            const sel = seleccionada?.id === p.id;
                            return (
                              <li
                                key={p.id}
                                onClick={() => setSeleccionada(p)}
                                className={
                                  'px-4 py-3 cursor-pointer transition-colors ' +
                                  (sel
                                    ? 'bg-primary/10 border-l-2 border-l-primary'
                                    : 'hover:bg-slate-50 border-l-2 border-l-transparent')
                                }
                              >
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <code className={
                                    'text-[11px] font-mono truncate ' +
                                    (sel ? 'text-primary font-medium' : 'text-slate-700')
                                  }>
                                    {p.tipo}
                                  </code>
                                  <div className="flex items-center gap-1 shrink-0">
                                    {!p.usuario_puede_silenciar && (
                                      <span title="Obligatoria" className="text-amber-500">
                                        <Lock className="w-3 h-3" />
                                      </span>
                                    )}
                                    {activa ? (
                                      <span title="Activa" className="text-emerald-500">
                                        <Eye className="w-3 h-3" />
                                      </span>
                                    ) : (
                                      <span title="Inactiva" className="text-slate-300">
                                        <EyeOff className="w-3 h-3" />
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {p.descripcion && (
                                  <p className="text-[11px] text-slate-500 line-clamp-2">
                                    {p.descripcion}
                                  </p>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* EDITOR */}
            <Card className="overflow-hidden p-0 lg:col-span-2 min-h-[600px] flex flex-col">
              {seleccionada ? (
                <EditorPlantilla
                  key={seleccionada.id}
                  plantilla={seleccionada}
                  onGuardado={onPlantillaActualizada}
                />
              ) : (
                <div className="flex-1 grid place-items-center text-sm text-slate-400">
                  Selecciona una plantilla para editarla.
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
